import { GoLink, Monogram, Section, displayTitle } from "@/features/platform/components/landing-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

type Standing = "active" | "ending" | "unpaid";

const standingTone: Record<Standing, string> = {
  active: "bg-primary/15 text-[var(--site-ink)]",
  ending: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  unpaid: "bg-destructive/15 text-destructive",
};

/** Sample clients and their passes. Nobody's real money. */
const clients = () => [
  { id: "ola", name: "Ola Kowalska", used: 7, total: 10, standing: "active" as const },
  { id: "kuba", name: "Kuba Mazur", used: 9, total: 10, standing: "ending" as const },
  { id: "ania", name: "Ania Wiśniewska", used: 4, total: 8, standing: "unpaid" as const },
  { id: "marek", name: "Marek Zieliński", used: 2, total: 10, standing: "active" as const },
];

const standingLabel = (standing: Standing) =>
  standing === "active"
    ? m.platform_pass_active()
    : standing === "ending"
      ? m.platform_pass_ending()
      : m.platform_pass_unpaid();

/**
 * The page's one loud band: passes and who has paid, the part of the business a trainer otherwise
 * keeps in a notebook. Solid primary, so it reads as the turn from "what it does" to "start".
 */
export const PassesBand = () => (
  <Section id="passes">
    <div className="relative isolate grid items-center gap-10 overflow-hidden rounded-[32px] bg-primary p-6 text-primary-foreground after:absolute after:-top-2/5 after:-right-[10%] after:-z-10 after:aspect-square after:w-3/5 after:rounded-full after:[background:repeating-radial-gradient(circle,transparent_0_26px,color-mix(in_oklch,var(--primary-foreground)_7%,transparent)_26px_27px)] md:p-14 lg:grid-cols-2 lg:gap-12">
      <div>
        <h2 className={displayTitle}>
          {m.platform_passes_title_lead()}
          <br />
          <span className="opacity-55">{m.platform_passes_title_tail()}</span>
        </h2>
        <p className="mt-5 max-w-[440px] text-lg leading-[1.55] opacity-75">{m.platform_passes_body()}</p>
        <GoLink to="/signup" className="mt-8">
          {m.platform_hero_cta()}
        </GoLink>
      </div>

      <div
        aria-hidden
        className="grid gap-2 rounded-3xl bg-background p-5 text-foreground shadow-[0_40px_80px_-30px_rgb(0_0_0/0.5)]"
      >
        <b className="px-1.5 pt-1 pb-2.5 text-lg font-semibold tracking-[-0.02em]">
          {m.platform_feature_packs_title()}
        </b>
        {clients().map((client) => (
          <div
            key={client.id}
            className="grid grid-cols-[40px_1fr_auto] items-center gap-3 rounded-2xl bg-[var(--site-well)] p-3"
          >
            <Monogram name={client.name} className="size-10 rounded-xl text-[13px]" />
            <span className="min-w-0">
              <strong className="block truncate text-[15px] font-semibold">{client.name}</strong>
              <span className="mt-1.5 flex items-center gap-2">
                <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <i
                    className="block h-full rounded-full bg-primary"
                    style={{ width: `${(client.used / client.total) * 100}%` }}
                  />
                </span>
                <small className="text-xs text-muted-foreground tabular-nums">
                  {client.used}/{client.total}
                </small>
              </span>
            </span>
            <span className={cn("rounded-full px-2.5 py-1 text-[11px] font-semibold", standingTone[client.standing])}>
              {standingLabel(client.standing)}
            </span>
          </div>
        ))}
      </div>
    </div>
  </Section>
);
